import { useEffect, useState, type FormEvent } from "react";
import { Button } from "./Button";
import { Chip } from "./Chip";
import { Icon } from "./Icon";
import { Modal } from "./Modal";
import { TextInput } from "./TextInput";
import { useSubmitFeedback } from "../lib/useSubmitFeedback";
import styles from "./FormModal.module.css";

interface SuggestSpotModalProps {
  open: boolean;
  onClose: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// 8-point compass — matches the chip layout in Figma 273:10505 (two
// rows of four, clockwise from N). Stored as ordered tuples so the
// submitted list is stable regardless of click order.
const DIRECTIONS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"] as const;
type Direction = (typeof DIRECTIONS)[number];

/**
 * Suggest-a-spot modal — Figma node 273:10640 (right card).
 * Captures the minimum data needed to add a new spot to `spots.json`:
 * name, location (Maps link or coords), best wind directions, and
 * free-form notes. Email is optional. POSTs to `/api/feedback` with
 * `kind: "suggest_spot"`; the success state swaps the form for a
 * thank-you message and a Close button.
 */
export function SuggestSpotModal({ open, onClose }: SuggestSpotModalProps) {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [dirs, setDirs] = useState<readonly Direction[]>([]);
  const [notes, setNotes] = useState("");
  const [email, setEmail] = useState("");
  const [honey, setHoney] = useState("");
  const [emailError, setEmailError] = useState(false);
  const { state, submit, reset } = useSubmitFeedback();

  useEffect(() => {
    if (!open) {
      setName("");
      setLocation("");
      setDirs([]);
      setNotes("");
      setEmail("");
      setHoney("");
      setEmailError(false);
      reset();
    }
  }, [open, reset]);

  const toggleDir = (d: Direction) =>
    setDirs((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d],
    );

  const canSubmit =
    name.trim().length > 0 &&
    location.trim().length > 0 &&
    dirs.length > 0 &&
    notes.trim().length > 0 &&
    state !== "sending";

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (email && !EMAIL_RE.test(email)) {
      setEmailError(true);
      return;
    }
    setEmailError(false);
    // Keep directions in canonical (clockwise) order regardless of
    // click order — easier to scan the email at the other end.
    const orderedDirs = DIRECTIONS.filter((d) => dirs.includes(d));
    void submit({
      kind: "suggest_spot",
      message: notes.trim(),
      spotName: name.trim(),
      location: location.trim(),
      directions: orderedDirs,
      email: email.trim() || undefined,
      ...(honey ? { _honey: honey } : {}),
    } as Parameters<typeof submit>[0]);
  };

  const sent = state === "sent";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={sent ? "Thanks for the spot" : "Suggest a spot"}
      description={
        sent
          ? "Got it, your suggestion is in the queue 🤙"
          : "Know a spot Big Wind should cover? Send it over. The more local the better. Tell me where it is, what it works on, and any quirks worth knowing."
      }
    >
      {sent ? (
        <div className={styles.actions}>
          <hr className={styles.divider} />
          <Button variant="elevated" onClick={onClose}>
            Close
          </Button>
        </div>
      ) : (
        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <div className={styles.fields}>
            <TextInput
              label="Spot name*"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Torö Stenstrand"
              disabled={state === "sending"}
            />
            <TextInput
              label="Google Maps link or coordinates*"
              required
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="https://maps.google.com/… or 58.83, 17.85"
              hint="From Google Maps: right-click the spot → click the coords to copy. Or paste the share link."
              disabled={state === "sending"}
            />
            <div className={styles.chipGroup} role="group" aria-label="Best wind directions">
              <p className={styles.chipLabel}>Best wind directions*</p>
              <div className={styles.chipRows}>
                {DIRECTIONS.map((d) => (
                  <Chip
                    key={d}
                    selected={dirs.includes(d)}
                    onClick={() => toggleDir(d)}
                    disabled={state === "sending"}
                  >
                    {d}
                  </Chip>
                ))}
              </div>
            </div>
            <TextInput
              multiline
              label="Notes*"
              required
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Flat to choppy bay, rocky point 30 m off the launch, clear once you're past it. Free roadside parking, no facilities."
              disabled={state === "sending"}
            />
            <TextInput
              type="email"
              autoComplete="email"
              inputMode="email"
              label="Your email (optional)"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(false);
              }}
              error={emailError}
              hint={
                emailError
                  ? "Doesn't look like a valid email."
                  : "Only if you'd like a follow-up."
              }
              disabled={state === "sending"}
            />
            <label className={styles.honeypot} aria-hidden tabIndex={-1}>
              Don't fill this in
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honey}
                onChange={(e) => setHoney(e.target.value)}
              />
            </label>
            {state === "error" && (
              <p className={styles.errorBanner} role="alert">
                Couldn't send. Check your connection and try again, or
                email info@bigwind.surf directly.
              </p>
            )}
          </div>
          <div className={styles.actions}>
            <hr className={styles.divider} />
            <Button
              type="submit"
              variant="elevated"
              disabled={!canSubmit}
            >
              <Icon name="send" />
              {state === "sending" ? "Sending…" : "Send"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
